"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Bell, Check, UserPlus, Users, Send, Coins } from "lucide-react";
const READERS = [{
  initials: "AK",
  color: "bg-indigo-500"
}, {
  initials: "MW",
  color: "bg-purple-500"
}, {
  initials: "JR",
  color: "bg-emerald-500"
}, {
  initials: "TS",
  color: "bg-rose-500"
}, {
  initials: "LN",
  color: "bg-amber-500"
}, {
  initials: "CE",
  color: "bg-sky-500"
}];
const START_FOLLOWERS = 842;
const END_FOLLOWERS = 918;
export function AudienceDemo() {
  const $ = _c(74);
  const [step, setStep] = useState(0);
  const [followers, setFollowers] = useState(START_FOLLOWERS);
  const containerRef = useRef(null);
  const cancelledRef = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      cancelledRef.current = false;
      const wait = _temp;
      async function runAnimation() {
        const isCancelled = () => cancelledRef.current;
        setStep(0);
        setFollowers(START_FOLLOWERS);
        await wait(500);
        if (isCancelled()) {
          return;
        }
        setStep(1);
        await wait(1100);
        if (isCancelled()) {
          return;
        }
        setStep(2);
        for (let n = START_FOLLOWERS; n <= END_FOLLOWERS; n = n + 4, n) {
          if (isCancelled()) {
            return;
          }
          setFollowers(n);
          await wait(45);
        }
        setFollowers(END_FOLLOWERS);
        await wait(900);
        if (isCancelled()) {
          return;
        }
        setStep(3);
        await wait(1100);
        if (isCancelled()) {
          return;
        }
        setStep(4);
        await wait(5200);
        if (isCancelled()) {
          return;
        }
        if (!isCancelled()) {
          runAnimation();
        }
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      }, {
        threshold: 0.4
      });
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
      return () => {
        cancelledRef.current = true;
        observer.disconnect();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = step >= 1 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("rounded-2xl border border-border bg-card/90 p-4 shadow-inner backdrop-blur-xl transition-all duration-700", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand to-brand-light font-heading text-lg font-bold text-white">E</div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="text-sm font-bold text-foreground">Elandor</div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Users className="size-3" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== followers) {
    t8 = followers.toLocaleString();
    $[8] = followers;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <div className="min-w-0 flex-1">{t6}<div className="flex items-center gap-1.5 text-xs text-muted-foreground">{t7}<span className="font-semibold tabular-nums text-foreground">{t8}</span>followers</div></div>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = step >= 2 ? "border border-border bg-background text-muted-foreground" : "bg-brand text-white shadow-sm shadow-brand/30";
  let t11;
  if ($[12] !== t10) {
    t11 = cn("flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-300", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== step) {
    t12 = step >= 2 ? <><Check className="size-3.5" strokeWidth={3} />Following</> : <><UserPlus className="size-3.5" />Follow</>;
    $[14] = step;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t11 || $[17] !== t12) {
    t13 = <button type="button" tabIndex={-1} aria-hidden="true" className={t11}>{t12}</button>;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13 || $[20] !== t9) {
    t14 = <div className="flex items-center gap-3">{t5}{t9}{t13}</div>;
    $[19] = t13;
    $[20] = t9;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t4) {
    t15 = <div className={t4}>{t14}</div>;
    $[22] = t14;
    $[23] = t4;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = step >= 3 ? "max-h-24 translate-y-0 opacity-100" : "max-h-0 translate-y-2 opacity-0";
  let t17;
  if ($[25] !== t16) {
    t17 = cn("overflow-hidden rounded-2xl border border-brand/30 bg-brand/5 transition-all duration-500", t16);
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-brand"><Send className="size-4" /></div>;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="min-w-0 flex-1"><div className="text-xs font-bold text-foreground">New chapter published</div><div className="truncate text-[11px] text-muted-foreground">Chapter 8 — The Reckoning</div></div>;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = step >= 4 ? "opacity-100" : "opacity-0";
  let t21;
  if ($[29] !== t20) {
    t21 = cn("flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-500 transition-opacity duration-500", t20);
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Bell className="size-3" />;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t21) {
    t23 = <div className="flex items-center gap-3 p-3.5">{t18}{t19}<span className={t21}>{t22}Sent</span></div>;
    $[32] = t21;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== t17 || $[35] !== t23) {
    t24 = <div className={t17}>{t23}</div>;
    $[34] = t17;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Your readers — notified instantly</div>;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== step) {
    t26 = READERS.map((r_0, i) => <div key={r_0.initials} className="relative"><div className={cn("flex size-10 items-center justify-center rounded-full text-xs font-bold text-white transition-all duration-500", r_0.color, step >= 4 ? "scale-100 opacity-100" : "scale-90 opacity-40")} style={{
        transitionDelay: `${i * 130}ms`
      }}>{r_0.initials}</div><div className={cn("absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-accent text-white shadow-sm transition-all duration-300", step >= 4 ? "scale-100 opacity-100" : "scale-0 opacity-0")} style={{
        transitionDelay: `${i * 130 + 200}ms`
      }}><Bell className="size-2.5" fill="currentColor" /></div></div>);
    $[38] = step;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t26) {
    t27 = <div className="rounded-2xl border border-border bg-card/60 p-4 backdrop-blur-xl">{t25}<div className="flex items-center justify-between">{t26}</div></div>;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== t15 || $[43] !== t24 || $[44] !== t27) {
    t28 = <div className="relative w-full max-w-2xl flex-1 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-900/20 via-background to-purple-900/10 p-4 shadow-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent">{t2}<div className="relative space-y-4">{t15}{t24}{t27}</div></div>;
    $[42] = t15;
    $[43] = t24;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  let t30;
  let t31;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand"><Users className="size-3.5" />Own your audience</div>;
    t30 = <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">Your readers follow you — not an algorithm</h2>;
    t31 = <p className="text-lg leading-relaxed text-muted-foreground">No feed burying your chapters, no middleman between you and your fans. Readers follow your page, get notified the moment you post, and every coin they spend goes straight to you.</p>;
    $[46] = t29;
    $[47] = t30;
    $[48] = t31;
  } else {
    t29 = $[46];
    t30 = $[47];
    t31 = $[48];
  }
  const t32 = step >= 4 ? "opacity-100" : "opacity-40";
  let t33;
  if ($[49] !== t32) {
    t33 = cn("flex items-start gap-3 transition-opacity duration-700", t32);
    $[49] = t32;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  let t35;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="mt-1 shrink-0 rounded-full bg-indigo-500/20 p-1 text-indigo-400"><Bell className="size-4" /></div>;
    t35 = <div><span className="font-semibold text-foreground">Direct notifications:</span>{" "}Followers hear the instant you publish — no algorithm in between.</div>;
    $[51] = t34;
    $[52] = t35;
  } else {
    t34 = $[51];
    t35 = $[52];
  }
  let t36;
  if ($[53] !== t33) {
    t36 = <li className={t33}>{t34}{t35}</li>;
    $[53] = t33;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  const t37 = step >= 2 ? "opacity-100" : "opacity-40";
  let t38;
  if ($[55] !== t37) {
    t38 = cn("flex items-start gap-3 transition-opacity duration-700", t37);
    $[55] = t37;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  let t40;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="mt-1 shrink-0 rounded-full bg-purple-500/20 p-1 text-purple-400"><Users className="size-4" /></div>;
    t40 = <div><span className="font-semibold text-foreground">You own the relationship:</span>{" "}Link your page from anywhere — your readers come with you.</div>;
    $[57] = t39;
    $[58] = t40;
  } else {
    t39 = $[57];
    t40 = $[58];
  }
  let t41;
  if ($[59] !== t38) {
    t41 = <li className={t38}>{t39}{t40}</li>;
    $[59] = t38;
    $[60] = t41;
  } else {
    t41 = $[60];
  }
  const t42 = step >= 3 ? "opacity-100" : "opacity-40";
  let t43;
  if ($[61] !== t42) {
    t43 = cn("flex items-start gap-3 transition-opacity duration-700", t42);
    $[61] = t42;
    $[62] = t43;
  } else {
    t43 = $[62];
  }
  let t44;
  let t45;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className="mt-1 shrink-0 rounded-full bg-emerald-500/20 p-1 text-emerald-400"><Coins className="size-4" /></div>;
    t45 = <div><span className="font-semibold text-foreground">Coins go straight to you:</span>{" "}Every unlock lands in your wallet — you keep 93%.</div>;
    $[63] = t44;
    $[64] = t45;
  } else {
    t44 = $[63];
    t45 = $[64];
  }
  let t46;
  if ($[65] !== t43) {
    t46 = <li className={t43}>{t44}{t45}</li>;
    $[65] = t43;
    $[66] = t46;
  } else {
    t46 = $[66];
  }
  let t47;
  if ($[67] !== t36 || $[68] !== t41 || $[69] !== t46) {
    t47 = <div className="max-w-lg flex-1 space-y-6">{t29}{t30}{t31}<ul className="mt-8 space-y-4">{t36}{t41}{t46}</ul></div>;
    $[67] = t36;
    $[68] = t41;
    $[69] = t46;
    $[70] = t47;
  } else {
    t47 = $[70];
  }
  let t48;
  if ($[71] !== t28 || $[72] !== t47) {
    t48 = <section ref={containerRef} className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-12 px-6 pt-24 pb-12 md:flex-row lg:px-12">{t28}{t47}</section>;
    $[71] = t28;
    $[72] = t47;
    $[73] = t48;
  } else {
    t48 = $[73];
  }
  return t48;
}
function _temp(ms) {
  return new Promise(r => setTimeout(r, ms));
}